import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, XCircle, Loader2, Copy } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

function CodeReveal({ email, code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(code); setCopied(true); };
  return (
    <div data-testid="payment-success">
      <div className="w-14 h-14 rounded-full bg-[#E5F0EA] flex items-center justify-center"><Check size={28} className="text-[#2D5F4F]" /></div>
      <p className="mt-4 text-[13.5px] text-[#3A4A44]">Your subscription is active. Next time, sign in with <strong data-testid="payment-email">{email}</strong> and this code:</p>
      <button onClick={copy} data-testid="payment-code"
        className="mt-4 w-full h-20 rounded-xl bg-[#F5EFDD] border border-[#E5DCC4] font-mono text-[40px] tracking-[0.4em] text-[#1A2622] inline-flex items-center justify-center gap-3 hover:bg-[#EFE6CF] transition-colors">
        {code} <Copy size={16} className="text-[#7A857F]" />
      </button>
      <div className="mt-2 text-[11.5px] text-[#7A857F]">{copied ? 'Copied!' : 'Tap to copy. Save it somewhere safe.'}</div>
      <Link to="/home" data-testid="payment-continue-btn"
        className="mt-6 w-full h-11 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[14px] font-medium inline-flex items-center justify-center hover:bg-[#2D5F4F] transition-colors">
        Start exploring →
      </Link>
    </div>
  );
}

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { refresh } = useAuth();
  const [state, setState] = useState(sessionId ? 'checking' : 'error');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let attempts = 0;
    async function poll() {
      try {
        const s = await api.paymentStatus(sessionId);
        if (cancelled) return;
        if (s.payment_status === 'paid') {
          const r = await api.claimCode(sessionId);
          if (cancelled) return;
          setResult(r); setState('success'); refresh();
          return;
        }
        if (s.status === 'expired') { setState('error'); return; }
      } catch (e) {
        if (e?.response?.status === 404 && !cancelled) { setState('error'); return; }
      }
      attempts += 1;
      if (attempts >= 15) { setState('pending'); return; }
      setTimeout(poll, 2000);
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const titles = { checking: 'Confirming your payment…', success: "You're in", pending: 'Payment still processing', error: 'We could not find that payment' };
  return (
    <AuthShell compactMobile title={titles[state]} subtitle={state === 'checking' ? 'This usually takes a few seconds.' : ''}>
      {state === 'checking' && <Loader2 size={36} className="text-[#4A7C5E] animate-spin" data-testid="payment-checking" />}
      {state === 'success' && result && <CodeReveal email={result.email} code={result.code} />}
      {state === 'pending' && (
        <p className="text-[13px] text-[#5A6660]" data-testid="payment-pending">Stripe is taking longer than usual. Refresh this page in a minute — your code will appear here.</p>
      )}
      {state === 'error' && (
        <p className="text-[13px] text-[#5A6660]" data-testid="payment-error">
          No charge was confirmed. <Link to="/pricing" className="text-[#4A7C5E] underline">Back to pricing</Link>
        </p>
      )}
    </AuthShell>
  );
}

export function PaymentCancelPage() {
  return (
    <AuthShell compactMobile title="Checkout cancelled" subtitle="No charge was made.">
      <div data-testid="payment-cancelled">
        <XCircle size={30} className="text-[#A06628]" />
        <div className="mt-5 flex gap-3">
          <Link to="/pricing" className="h-11 px-5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[13px] inline-flex items-center hover:bg-[#2D5F4F]" data-testid="payment-back-pricing">Back to pricing</Link>
          <Link to="/login" className="h-11 px-5 rounded-md border border-[#E5DCC4] text-[#3A4A44] text-[13px] inline-flex items-center hover:bg-[#F5EFDD]">Sign in</Link>
        </div>
      </div>
    </AuthShell>
  );
}
